'use client';

import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useSession } from '../hooks/useSession';
import type { User } from '../lib/types';

export interface AccountAvatarProps {
  /** Avatar diameter in pixels (default: 40) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
  /** ID of element to toggle visibility on click (e.g., IFRAME container) */
  targetId?: string;
  /** Custom click handler */
  onClick?: () => void;
  /** Callback when avatar is clicked with current user data */
  onAvatarClick?: (user: User | null) => void;
}

/**
 * AccountAvatar Component
 *
 * A compact, clickable avatar component for NAV bars that displays the current user's avatar
 * and synchronizes with the AccountSwitcher session.
 *
 * Features:
 * - Displays user avatar (image or initials) when logged in
 * - Shows generic profile icon when logged out
 * - Automatically syncs with session changes via AccountSwitcherProvider
 * - Toggles target element visibility (typically IFRAME container)
 * - Outside click and ESC key support for closing popup
 * - Smooth hover and active animations
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AccountAvatar />
 *
 * // With IFRAME toggle
 * <AccountAvatar targetId="account-switcher-dropdown" size={40} />
 *
 * // In NAV with dropdown
 * <nav>
 *   <AccountAvatar targetId="switcher-popup" />
 * </nav>
 * <div id="switcher-popup" style={{ display: 'none' }}>
 *   <AccountSwitcher />
 * </div>
 * ```
 */
export function AccountAvatar({
  size = 40,
  className = '',
  style = {},
  targetId,
  onClick,
  onAvatarClick,
}: AccountAvatarProps) {
  const { currentUser, isAuthenticated } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId();
  const avatarClass = `account-avatar-${uniqueId.replace(/:/g, '-')}`;

  /**
   * Get initials from name (same logic as IFRAME)
   */
  const getInitials = useCallback((name: string): string => {
    if (!name) return '??';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, []);

  /**
   * Handle avatar image error (fallback to initials)
   */
  const [imageError, setImageError] = useState(false);
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  /**
   * Reset image error when user changes
   */
  useEffect(() => {
    setImageError(false);
  }, [currentUser?.uuid]);

  /**
   * Render avatar content (image or initials)
   */
  const renderAvatarContent = useCallback(() => {
    if (!isAuthenticated || !currentUser) {
      return null;
    }

    // If avatar exists and hasn't errored, show image
    if (currentUser.avatar && !imageError) {
      const src = currentUser.avatar.startsWith('http')
        ? currentUser.avatar
        : `/storage/${currentUser.avatar}`;

      return (
        <img
          src={src}
          alt={currentUser.name}
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
          }}
        />
      );
    }

    // Fallback to initials
    return getInitials(currentUser.name);
  }, [isAuthenticated, currentUser, imageError, getInitials, handleImageError]);

  /**
   * Handle click events
   */
  const handleClick = useCallback(() => {
    // Call onAvatarClick callback with user data
    onAvatarClick?.(currentUser);

    // Toggle target element if specified
    if (targetId) {
      const target = document.getElementById(targetId);
      if (target) {
        const willOpen = !isOpen;
        target.style.display = willOpen ? 'block' : 'none';
        setIsOpen(willOpen);
      }
    }

    // Call custom onClick if provided
    onClick?.();
  }, [currentUser, targetId, isOpen, onAvatarClick, onClick]);

  /**
   * Setup outside click and ESC key handlers when popup is open
   */
  useEffect(() => {
    if (!isOpen || !targetId || typeof window === 'undefined') return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const clickedInside = target.contains(event.target as Node);
      const clickedAvatar = avatarRef.current?.contains(event.target as Node);

      if (!clickedInside && !clickedAvatar) {
        target.style.display = 'none';
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const target = document.getElementById(targetId);
        if (target) {
          target.style.display = 'none';
          setIsOpen(false);
        }
      }
    };

    // Delay adding listener to prevent immediate closure from same click
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, targetId]);

  /**
   * Inject hover and active styles
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const styleId = `account-avatar-styles-${avatarClass}`;

    // Check if styles already exist
    if (document.getElementById(styleId)) return;

    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = `
      .${avatarClass}:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      .${avatarClass}:active {
        transform: scale(0.98);
      }
    `;

    document.head.appendChild(styleElement);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [avatarClass]);

  // Container styles
  const containerStyle: React.CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: isAuthenticated && currentUser ? 'transparent' : '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0,
    ...style,
  };

  // Icon styles (shown when logged out)
  const iconStyle: React.CSSProperties = {
    transition: 'opacity 0.2s ease',
    display: isAuthenticated && currentUser ? 'none' : 'block',
  };

  // Content styles (shown when logged in)
  const contentStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: isAuthenticated && currentUser ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      currentUser?.avatar && !imageError
        ? 'transparent'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    fontSize: `${size * 0.4}px`,
    fontWeight: 500,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  return (
    <div
      ref={avatarRef}
      className={`${avatarClass} ${className}`}
      style={containerStyle}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={
        currentUser
          ? `Account menu for ${currentUser.name}`
          : 'Account menu'
      }
    >
      {/* Generic profile icon (default state) */}
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#9ca3af"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={iconStyle}
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>

      {/* Avatar content (shown when logged in) */}
      <div style={contentStyle}>{renderAvatarContent()}</div>
    </div>
  );
}
